// src/ElectricalOdorPage.js
import React from 'react';
import { dedupeChecklist } from './utils/dedupeChecklist';

export default function ElectricalOdorPage({
  electricalChecklist = [],
  odorChecklist = [],
  obdcData = null,
}) {
  /* -------------------------------------------------------- */
  /*  CONSTANTS                                               */
  /* -------------------------------------------------------- */
  const colorBlue  = '#153BC6';
  const lightGray1 = '#f7f7f7';
  const lightGray2 = '#eaeaea';

  /* -------------------------------------------------------- */
  /*  HELPERS                                                 */
  /* -------------------------------------------------------- */
  const scrollTo = (id) => {
    const target =
      document.getElementById(id) || document.getElementById('damage-report');
    if (target) target.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  function renderDamageIcon(sev) {
    const base = { display: 'inline-block', verticalAlign: 'middle' };

    if (sev === 'Light') {
      return (
        <img
          src="/MagnifyingGlass.png"
          alt="Inspect"
          style={{ ...base, width: '1.4rem', height: '1.4rem' }}
        />
      );
    }
    if (sev === 'Moderate') {
      return <span style={{ ...base, color: 'orange', fontSize: '1.4rem' }}>&#9888;</span>;
    }
    if (sev === 'Severe') {
      return <span style={{ ...base, color: 'red', fontSize: '1.8rem' }}>&#10007;</span>;
    }
    return <span style={{ ...base, color: 'green', fontSize: '1.4rem' }}>&#10003;</span>;
  }

  const tableStyle = { width: '100%', borderCollapse: 'collapse' };
  const td         = { padding: '4px 8px', verticalAlign: 'middle' };

  /* -------------------------------------------------------- */
  /*  NORMALISE OBD DATA                                      */
  /* -------------------------------------------------------- */
  const obdRows = Array.isArray(obdcData) ? obdcData : obdcData ? [obdcData] : [];
  const distanceSinceReset =
    obdRows.length > 0 ? obdRows[0].DistanceSinceDTCReset ?? null : null;

  /* -------------------------------------------------------- */
  /*  RENDER                                                  */
  /* -------------------------------------------------------- */
  return (
    <div
      className="report-page"
      style={{
        width: '8.5in',
        minHeight: '11in',
        margin: '0 auto',
        padding: '0.3in',
        paddingBottom: '1in',
        backgroundColor: '#fff',
        border: '1px solid #ccc',
        boxSizing: 'border-box',
        fontFamily: 'Arial, sans-serif',
        position: 'relative',
      }}
    >
      <h2 style={{ marginBottom: '0.2in', color: colorBlue }}>
        Electrical &amp; Odor Inspection
      </h2>

      {/* ==================================================== */}
      {/*  TWO‑COLUMN CHECKLIST SECTION                        */}
      {/* ==================================================== */}
      <div style={{ display: 'flex', gap: '10px' }}>
        {/* ---------- Electrical column ---------- */}
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '20px' }}>
          <div style={{ borderLeft: `5px solid ${colorBlue}`, paddingLeft: 8 }}>
            <h3 style={{ color: colorBlue, marginBottom: 4, fontSize: '1rem' }}>
              Electrical Checklist
            </h3>
            <table style={tableStyle}>
              <tbody>
                {electricalChecklist.length === 0 ? (
                  <tr>
                    <td style={td}>No Items Found.</td>
                  </tr>
                ) : (
                  dedupeChecklist(electricalChecklist).map((item, idx) => {
                    const bg = idx % 2 === 0 ? lightGray1 : lightGray2;
                    const clickable = ['Light', 'Moderate', 'Severe'].includes(item.DamageSeverity);
                    return (
                      <tr key={item.DropdownValueId} style={{ backgroundColor: bg }}>
                        <td style={td}>{item.DropdownValue}</td>
                        <td
                          style={{
                            ...td,
                            textAlign: 'center',
                            cursor: clickable ? 'pointer' : 'default',
                          }}
                          onClick={clickable ? () => scrollTo('damage-electrical-damage') : undefined}
                          title={clickable ? 'Jump to Damage Report' : undefined}
                        >
                          {renderDamageIcon(item.DamageSeverity)}
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* ---------- Odor column ---------- */}
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '20px' }}>
          <div style={{ borderLeft: `5px solid ${colorBlue}`, paddingLeft: 8 }}>
            <h3 style={{ color: colorBlue, marginBottom: 4, fontSize: '1rem' }}>
              Odor Checklist
            </h3>
            <table style={tableStyle}>
              <tbody>
                {odorChecklist.length === 0 ? (
                  <tr>
                    <td style={td}>No Items Found.</td>
                  </tr>
                ) : (
                  dedupeChecklist(odorChecklist).map((item, idx) => {
                    const bg = idx % 2 === 0 ? lightGray1 : lightGray2;
                    const clickable = ['Light', 'Moderate', 'Severe'].includes(item.DamageSeverity);
                    return (
                      <tr key={item.DropdownValueId} style={{ backgroundColor: bg }}>
                        <td style={td}>{item.DropdownValue}</td>
                        <td
                          style={{
                            ...td,
                            textAlign: 'center',
                            cursor: clickable ? 'pointer' : 'default',
                          }}
                          onClick={clickable ? () => scrollTo('damage-odor-damage') : undefined}
                          title={clickable ? 'Jump to Damage Report' : undefined}
                        >
                          {renderDamageIcon(item.DamageSeverity)}
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

          {/* ---------- Notes ---------- */}
          <div
            style={{
              borderLeft: `5px solid ${colorBlue}`,
              paddingLeft: 8,
              backgroundColor: '#f9f9f9',
              padding: '10px',
            }}
          >
            <h3 style={{ color: colorBlue, marginBottom: 6, fontSize: '1rem' }}>
              Important Electrical &amp; Odor Notes
            </h3>
            <p style={{ margin: '6px 0' }}>If the vehicle has a button—we’ve pressed it.</p>
            <p style={{ margin: '6px 0' }}>
              We test all visible electronic features and controls for basic functionality,
              including windows, lights, locks, screens, and more.
            </p>
            <p style={{ margin: '6px 0' }}>
              If something doesn’t work, it’s noted. If it works, we’ve confirmed it through
              hands‑on testing—not guesses.
            </p>
          </div>
        </div>
      </div>

      {/* ==================================================== */}
      {/*  FULL‑WIDTH OBD BLOCK                                */}
      {/* ==================================================== */}
      {obdRows.length > 0 && (
        <div
          style={{
            marginTop: '20px',
            borderLeft: `5px solid ${colorBlue}`,
            paddingLeft: 8,
            backgroundColor: '#f9f9f9',
            padding: '10px',
            /* stretch across the page */
          }}
        >
          <h3 style={{ color: colorBlue, marginBottom: 6, fontSize: '1rem' }}>
            On‑Board Diagnostics
          </h3>

          {/* Distance */}
          {distanceSinceReset !== null && (
            <p style={{ margin: '6px 0', fontWeight: 600 }}>
              Distance Since DTC Reset:&nbsp;
              <span style={{ fontWeight: 400 }}>{distanceSinceReset} miles</span>
            </p>
          )}

          {/* Codes table */}
          <table style={{ ...tableStyle, marginTop: 8 }}>
            <thead>
              <tr style={{ backgroundColor: lightGray2 }}>
                <th style={{ ...td, textAlign: 'left' }}>Code</th>
                <th style={{ ...td, textAlign: 'left' }}>Description</th>
                <th style={{ ...td, textAlign: 'left' }}>Notes</th>
              </tr>
            </thead>
            <tbody>
              {obdRows.map((row, idx) => {
                const bg = idx % 2 === 0 ? lightGray1 : '#ffffff';
                return (
                  <tr key={`${row.Code}-${idx}`} style={{ backgroundColor: bg }}>
                    <td style={td}>{row.Code}</td>
                    <td style={td}>{row.Description || '--'}</td>
                    <td style={td}>{row.Notes || '—'}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* logo */}
      <div style={{ position: 'absolute', bottom: 10, right: 10 }}>
        <img src="/logo.png" alt="Company Logo" style={{ height: 40 }} />
      </div>
    </div>
  );
}
